import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate } from "@/lib/date";

export default function QueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const status = searchParams.get("status") || "";
  const scope = searchParams.get("scope") || "today";
  const navigate = useNavigate();

  const load = useCallback(() => {
    // Ordered by check-in time (first patient to check in shows first in the
    // queue), not by scheduled slot — patients don't always arrive in the order
    // they were booked. Recomputed fresh each poll so the 10s interval never
    // holds onto a stale "today" date across midnight.
    const params = { status, sort: "checkin_asc" };
    if (scope === "today") params.date = new Date().toISOString().slice(0, 10);
    api.get("/appointments", { params }).then(({ data }) => setAppointments(data.appointments)).catch(() => {});
  }, [status, scope]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  const setStatus = (v) => {
    const params = new URLSearchParams(searchParams);
    if (v === "ALL") params.delete("status");
    else params.set("status", v);
    setSearchParams(params);
  };

  const callNext = async (a) => {
    try {
      await api.put(`/appointments/${a.id}/status`, { status: "WITH_DOCTOR" });
      toast.success(`${a.patient_name} is now with the doctor`);
      navigate("/doctor/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <DoctorLayout>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="queue-heading">
            {scope === "today" ? "Today's Queue" : "All Appointments"}
          </h1>
          {scope !== "today" && (
            <button data-testid="queue-back-to-today" className="text-xs text-teal-700 hover:underline" onClick={() => setSearchParams({})}>
              Back to Today's Queue
            </button>
          )}
        </div>
        <Select value={status || "ALL"} onValueChange={setStatus}>
          <SelectTrigger data-testid="queue-status-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="SCHEDULED">Scheduled</SelectItem>
            <SelectItem value="WAITING">Waiting</SelectItem>
            <SelectItem value="WITH_DOCTOR">With Doctor</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
            <SelectItem value="FOLLOW_UP_SCHEDULED">Follow-up Scheduled</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-3">
        {appointments.map((a) => (
          <div
            key={a.id}
            onClick={() => navigate(`/doctor/patients/${a.patient_id}`)}
            className="flex cursor-pointer items-center justify-between card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-4 transition-colors hover:bg-teal-50/30"
            data-testid={`queue-card-${a.appointment_code}`}
          >
            <div className="flex items-center gap-3">
              <div>
                <p className="font-medium text-slate-900">{a.patient_name}</p>
                <p className="text-xs text-slate-500 font-mono">{a.appointment_code} · {formatDate(a.appointment_date)} · {a.appointment_time}</p>
              </div>
              <TypeBadge type={a.appointment_type} />
              <StatusBadge status={a.status} />
            </div>
            <div className="flex gap-2">
              {a.status === "WAITING" && (
                <Button
                  size="sm"
                  data-testid={`call-next-${a.appointment_code}`}
                  className="bg-teal-700 hover:bg-teal-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    callNext(a);
                  }}
                >
                  Call Next Patient
                </Button>
              )}
              {a.status === "WITH_DOCTOR" && (
                <Button
                  size="sm"
                  data-testid={`open-patient-${a.appointment_code}`}
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/doctor/patients/${a.patient_id}/consult/${a.id}`);
                  }}
                >
                  Open Patient
                </Button>
              )}
              {(a.status === "COMPLETED" || a.status === "FOLLOW_UP_SCHEDULED") && (
                <Button
                  size="sm"
                  data-testid={`view-patient-${a.appointment_code}`}
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/doctor/patients/${a.patient_id}`);
                  }}
                >
                  View Patient
                </Button>
              )}
            </div>
          </div>
        ))}
        {appointments.length === 0 && (
          <p className="text-sm text-slate-400" data-testid="queue-empty-state">
            No appointments found{scope === "today" ? " for today" : ""}.
          </p>
        )}
      </div>
    </DoctorLayout>
  );
}
